import { Component, inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { SelectOptionGroup } from 'app/_componentes/select/_models/selectOptionGroup';
import { MixPanelEventEnum } from 'app/_models/mixPanelEventEnum';
import { GeneralService } from 'app/_services/general.service';
import { getBrandIdByShortName } from 'app/_utils/general-utils';
import { GetVehicleResponseModel } from '../../_models/GetVehicleResponseModel';
import { ApiResponseModel } from 'app/_models/apiResponseModel';
import { BuyerCreateQuotationService } from '../../create-quotation.service';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { Globals } from 'app/_models/globals';
import { NotificationService } from 'app/_services/notification.service';
import { QuotationVehicle, quotationVehicleFactory } from '../../_models/quotationVehicle';
import { SweetAlertParams } from 'app/_models/notificationParams.model';
import { MixPanelService } from 'app/_services/mix-panel.service';

@Component({
    selector: 'quotation-vehicle-component', templateUrl: './quotation-vehicle.component.html', styleUrls: ['./quotation-vehicle.component.scss'],
    standalone: false
})
export class BuyerCreateQuotationVehicleComponent implements OnInit {
    public patternPlaca = new RegExp('^[a-zA-Z]{3}[0-9][A-Za-z0-9][0-9]{2}$')

    @Input() isQuotationWithParts: boolean = true;
	quotationVehicleCollapsed = false;
    loading: { brands: boolean, insurances: boolean, plate: boolean }
    plateLoaded: boolean = false;

    fb = inject(FormBuilder);
    maxYear = new Date().getFullYear() + 1;

    insuranceIdControl: FormControl<string>;
    licensePlateControl: FormControl<string>;
    vehicleNameControl: FormControl<string>;
    brandIdControl: FormControl<string>;
    yearControl: FormControl<string>;
    chassiControl: FormControl<string>;

    vehicleFormGroup = this.fb.group({
        insuranceId: [-1, [Validators.required, Validators.min(0)]],
        licensePlate: ["", [Validators.required, Validators.pattern(this.patternPlaca)]],
        vehicleName: ["", [Validators.required]],
        brandId: [-1, [Validators.required, Validators.min(1)]],
        year: [null as number, [Validators.required, Validators.min(1950), Validators.max(this.maxYear)]],
        chassi: ["", [Validators.required, Validators.pattern(/.{17}/)]]
    });

    public insuranceOptions: SelectOptionGroup[] = [];
    public brandOptions: SelectOptionGroup[] = [];
    public allBrands: any[] = [];

    private validationMessages: { insuranceId: any, licensePlate: any, vehicleName: any, brandId: any, year: any, chassi: any };

    constructor(private createQuotationService: BuyerCreateQuotationService, private generalService: GeneralService, private mixPanelService: MixPanelService, private notificationService: NotificationService, private ngxLoader: NgxUiLoaderService, private globals: Globals) {
        this.validationMessages = {
            insuranceId: {
                required: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.insurance.required'),
                min: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.insurance.required'),
            },
            licensePlate: {
                required: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.license-plate.required'),
                pattern: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.license-plate.pattern'),
            },
            vehicleName: {
                required: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.vehicle-name.required'),
            },
            brandId: {
                required: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.brand.required'),
                min: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.brand.required'),
            },
            year: {
                required: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.year.required'),
                min: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.year.min-max').replace('{maxYear}', this.maxYear.toString()),
                max: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.year.min-max').replace('{maxYear}', this.maxYear.toString()),
            },
            chassi: {
                required: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.chassi.required'),
                pattern: this.globals.translate('buyer.create-quotation.quotation-vehicle.form.validation-message.chassi.pattern'),
            }
        };
    }

    ngOnInit() {
        this.quotationVehicleCollapsed = !this.isQuotationWithParts;

        this.insuranceIdControl = this.vehicleFormGroup.get('insuranceId') as FormControl;
        this.licensePlateControl = this.vehicleFormGroup.get('licensePlate') as FormControl;
        this.vehicleNameControl = this.vehicleFormGroup.get('vehicleName') as FormControl;
        this.brandIdControl = this.vehicleFormGroup.get('brandId') as FormControl;
        this.yearControl = this.vehicleFormGroup.get('year') as FormControl;
        this.chassiControl = this.vehicleFormGroup.get('chassi') as FormControl;

        this.getBrandsAndInsuranceAsync()
    }


    clearVehicleFields() {
        this.vehicleFormGroup.patchValue({
            year: null,
            chassi: '',
            brandId: -1,
            licensePlate: '',
            vehicleName: ''
        });

        this.vehicleFormGroup.controls.licensePlate.enable();
        this.vehicleFormGroup.controls.licensePlate.markAsUntouched();

        this.vehicleFormGroup.controls.brandId.markAsUntouched();
        this.vehicleFormGroup.controls.chassi.markAsUntouched();
        this.vehicleFormGroup.controls.year.markAsUntouched();
        this.vehicleFormGroup.controls.vehicleName.markAsUntouched();

        this.vehicleFormGroup.controls.brandId.updateValueAndValidity();
        this.vehicleFormGroup.controls.chassi.updateValueAndValidity();
        this.vehicleFormGroup.controls.year.updateValueAndValidity();
        this.vehicleFormGroup.controls.vehicleName.updateValueAndValidity();
    }

    editFieldsApiPlate() {
        if (this.loading.plate)
            return;

        let eventProperties = { 'license_plate': this.vehicleFormGroup.controls.licensePlate.value.toUpperCase() };
        this.mixPanelService.trackMixPanelEvent(MixPanelEventEnum.UPlate, eventProperties).subscribe({ error: (e: any) => console.error(e) });
        this.clearVehicleFields();
    }


    getBrandsAndInsuranceAsync() {
        this.loading = { brands: true, insurances: true, plate: false };
        this.vehicleFormGroup.controls.brandId.disable();
        this.vehicleFormGroup.controls.insuranceId.disable();


        this.generalService.getBrands().subscribe({
            next: brands => {
                var mappedBrand = brands.map(i => { return { value: i.id, name: i.name, isPrincipal: i.is_principal } });

                this.brandOptions = [
                    mappedBrand.filter(i => i.isPrincipal),
                    mappedBrand
                ];

                this.allBrands = brands;

                this.loading.brands = false;
                this.vehicleFormGroup.controls.brandId.enable();
            },
            error: error => {
                console.log(error);
            }
        });

        this.generalService.getInsurances().subscribe({
            next: insurances => {
                var mappedInsurances = insurances.map(i => { return { value: i.id, name: i.name, isPrincipal: i.is_principal } });

                this.insuranceOptions = [
                    mappedInsurances.filter(i => i.isPrincipal),
                    mappedInsurances
                ];

                this.loading.insurances = false;
                this.vehicleFormGroup.controls.insuranceId.enable();
            },
            error: error => {
                console.log(error);
            }
        });
    }

    getVehicleData(): QuotationVehicle | null {
        if (!this.isFormValid())
            return null;

        const formValue = this.vehicleFormGroup.getRawValue();
        const part = quotationVehicleFactory(formValue.insuranceId, formValue.licensePlate, formValue.vehicleName, formValue.brandId, formValue.year, formValue.chassi)

        return part;
    }

    searchApiPlate() {
        if (this.vehicleFormGroup.controls.licensePlate.invalid)
            return;

        this.ngxLoader.startLoader('loader-search-plate');
        this.vehicleFormGroup.controls.licensePlate.disable();
        this.loading.plate = true;

        let eventProperties = { 'license_plate': this.vehicleFormGroup.controls.licensePlate.value.toUpperCase() };
        this.mixPanelService.trackMixPanelEvent(MixPanelEventEnum.CPlate, eventProperties).subscribe({ error: (e: any) => console.error(e) });

        this.createQuotationService.getVehicleByLicensePlate(this.vehicleFormGroup.controls.licensePlate.value).subscribe({
            next: (response: ApiResponseModel<GetVehicleResponseModel>) => {
                if (response && response.success) {
                    this.notificationService.showSuccessToastr(this.globals.translate('buyer.create-quotation.quotation-vehicle.api-plate.plate-found'));

                    let brandId = getBrandIdByShortName(this.allBrands, response.result.brand);
                    let chassi = (response.result.chassisExtra) ?? response.result.chassis;

                    if (chassi.includes('*'))
                        chassi = '';

                    this.vehicleFormGroup.patchValue({
                        vehicleName: response.result.vehicleName,
                        chassi: chassi,
                        year: parseInt(response.result.year),
                        brandId: brandId
                    });

                    this.vehicleFormGroup.controls.year.markAsTouched();
                    this.vehicleFormGroup.controls.year.updateValueAndValidity();

                    this.vehicleFormGroup.controls.chassi.markAsTouched();
                    this.vehicleFormGroup.controls.chassi.updateValueAndValidity();
                    this.plateLoaded = true;
                }

                this.ngxLoader.stopLoader('loader-search-plate');
                this.loading.plate = false;
            },
            error: async () => {
                this.ngxLoader.stopLoader('loader-search-plate');
                this.loading.plate = false;
                this.vehicleFormGroup.controls.licensePlate.enable();
                await this.notificationService.showWarning('buyer.create-quotation.quotation-vehicle.api-plate.plate-not-found', 'buyer.create-quotation.quotation-vehicle.api-plate.title');
                this.mixPanelService.trackMixPanelEvent(MixPanelEventEnum.LicensePlateNotFound, eventProperties).subscribe({ error: (e: any) => console.error(e) });
            }
        });
    }

    updateVehicleCollapse(isQuotationWithParts: boolean) {
        this.quotationVehicleCollapsed = !isQuotationWithParts;
        this.clearVehicleFields();
    }

    private isFormValid() {
        this.notificationService.clearToastr();

        Object.keys(this.vehicleFormGroup.controls).forEach(field => {
            const control = this.vehicleFormGroup.get(field);
            control?.markAsTouched({ onlySelf: true });
        });

        if (this.vehicleFormGroup.valid)
            return true;

        var message = '';

        Object.keys(this.validationMessages).forEach(field => {
            const control = this.vehicleFormGroup.get(field);
            const errorMessages = this.validationMessages[field];

            if (control?.invalid) {
                Object.keys(errorMessages).forEach(errorKey => {
                    if (control.errors?.[errorKey]) {
                        message += '<ul style="text-align: left"><li>' + errorMessages[errorKey] + '</li></ul>';
                    }
                });
            };
        });

        let params: SweetAlertParams = {
            bigSwall: true,
            icon: 'warning',
            isHtml: true,
            message: message,
            title: 'buyer.create-quotation.quotation-vehicle.form.validation-message.title',
        }

        this.notificationService.showInfoWithoutButtons(params);

        return false;
    }
}
